import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal.js';
import { Button } from '../ui/Button.js';
import { Input } from '../ui/Input.js';
import { Select } from '../ui/Select.js';
import { ContactSelector, type ContactSelectorValue } from '../ui/ContactSelector.js';
import { useToast } from '../ui/Toast.js';
import { useCreateProject } from '../../hooks/useProjects.js';
import { CreateContactForm } from '../contacts/CreateContactForm.js';

interface Props {
  open: boolean;
  onClose: () => void;
  preselectedContact?: { id: number; name: string } | null;
  leadId?: string | null;
}


const TYPE_OPTIONS = [{ value: 'website', label: 'Website' }];

// Website ya no se parte en subtipos: las páginas del sitio las define el
// brief (briefData.pages) y el plan de contenido se arma por página. El campo
// subtype queda informativo en proyectos viejos, sin efecto.
const SUBTYPE_OPTIONS: Record<string, Array<{ value: string; label: string }>> = {};

export function CreateProjectModal({ open, onClose, preselectedContact, leadId }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const createProject = useCreateProject();
  const [contact, setContact] = useState<ContactSelectorValue | null>(preselectedContact ?? null);
  const [projectName, setProjectName] = useState('');
  const [projectType, setProjectType] = useState('website');
  const [projectSubtype, setProjectSubtype] = useState<string | null>('single_page');
  const [showNewContact, setShowNewContact] = useState(false);

  useEffect(() => {
    if (open) {
      setContact(preselectedContact ?? null);
      setProjectName('');
      setProjectType('website');
      setProjectSubtype('single_page');
      setShowNewContact(false);
    }
  }, [open, preselectedContact]);

  useEffect(() => {
    const subtypes = SUBTYPE_OPTIONS[projectType] ?? [];
    setProjectSubtype(subtypes[0]?.value ?? null);
  }, [projectType]);

  const subtypeOptions = SUBTYPE_OPTIONS[projectType] ?? [];
  const isSubmitting = createProject.isPending;
  const canSubmit = contact !== null && projectName.trim().length > 0 && !isSubmitting;

  async function handleSubmit() {
    if (!contact) return;
    try {
      const result = await createProject.mutateAsync({
        contactId: contact.id,
        name: projectName.trim(),
        type: projectType,
        subtype: projectSubtype,
        leadId: leadId ?? null,
      });
      toast.show({ message: t('projects.create.success'), tone: 'success' });
      onClose();
      navigate(`/proyectos/${result.slug}`);
    } catch {
      toast.show({ message: t('common.error'), tone: 'error' });
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('projects.create.title')}
      size="sm"
      preventClose={isSubmitting}
      footer={
        <>
          <Button variant="outline" tone="muted" onClick={onClose} disabled={isSubmitting}>
            {t('projects.create.cancel')}
          </Button>
          <Button
            tone="cyan"
            onClick={() => { void handleSubmit(); }}
            disabled={!canSubmit}
            loading={isSubmitting}
          >
            {t('projects.create.submit')}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Contacto */}
        <div>
          <ContactSelector
            label={t('projects.create.contact')}
            value={contact}
            onChange={setContact}
            disabled={!!preselectedContact}
            placeholder={t('projects.create.contactPlaceholder')}
            fullWidth
          />
          {!preselectedContact && !showNewContact && (
            <Button variant="link" tone="cyan" size="sm" type="button" onClick={() => setShowNewContact(true)}>
              {t('projects.create.newContact')}
            </Button>
          )}

          {showNewContact && (
            <div style={{
              marginTop: '8px', padding: '12px',
              background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)',
              borderRadius: 'var(--evo-r)',
            }}>
              <CreateContactForm
                onSuccess={c => { setContact(c); setShowNewContact(false); }}
                onCancel={() => setShowNewContact(false)}
              />
            </div>
          )}
        </div>

        {/* Nombre del proyecto */}
        <Input
          label={t('projects.create.name')}
          value={projectName}
          onChange={setProjectName}
          placeholder={t('projects.create.namePlaceholder')}
          fullWidth
        />

        {/* Tipo */}
        <Select
          label={t('projects.create.type')}
          value={projectType}
          onChange={v => { if (v) setProjectType(v); }}
          options={TYPE_OPTIONS}
          fullWidth
        />

        {/* Subtipo */}
        {subtypeOptions.length > 0 && (
          <Select
            label={t('projects.create.subtype')}
            value={projectSubtype}
            onChange={v => setProjectSubtype(v)}
            options={subtypeOptions}
            fullWidth
          />
        )}
      </div>
    </Modal>
  );
}
